import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';
import { AppDispatch } from '../../store';
import { markStepComplete } from '../../store/clubChecklistSlice';
import { toPublicUrl, withCacheBuster } from './imageUtils';
import { usePostHogCapture } from '../../hooks/usePostHogCapture';
import useIsMobile from '../../hooks/useIsMobile';
import {
  buildChartConfig,
  fetchCsv,
  parseCsvText,
  validateGraphSpec,
  CsvRow,
  GraphSpec,
} from './graphUtils';
import { POSTHOG_EVENTS } from '../../utils/posthogEvents';
import { TIME_ZONE_OPTIONS } from '../../utils/timeZoneOptions';
import { ZOOM_MODIFIER_KEY } from '../forecastGraphs/graphutil';

// registerables pulls in every scale (incl. TimeScale) and the Filler plugin
// that fill-between bands rely on. zoomPlugin adds click-and-drag box zoom,
// matching the Asset View processed-model charts (ProcessedScatterGraph.tsx).
ChartJS.register(...registerables, zoomPlugin);

// Resolved once at module load (not per-render): the browser's IANA zone, used
// as the default for time-axis charts. Falls back to UTC if resolution fails
// (e.g. a test/SSR environment with no Intl timezone data).
const LOCAL_TIME_ZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
})();

// 'local' is a sentinel meaning "the viewer's browser zone" (LOCAL_TIME_ZONE);
// any other value is an IANA zone name (or 'UTC') picked from the dropdown.
type TimeZoneChoice = string;
const LOCAL_ZONE_VALUE = 'local';

// Persist the picker choice so a multi-chart reply doesn't need toggling per card.
const TIME_ZONE_STORAGE_KEY = 'graphChart.timeZone';
// Broadcast toggles so charts already mounted in the same reply stay in sync.
const TIME_ZONE_EVENT = 'graphChart:timeZone';

// Valid picker values: the local sentinel plus the curated IANA zones. Guards
// against a stale/removed zone in localStorage throwing in Intl.DateTimeFormat.
const VALID_TIME_ZONES = new Set<TimeZoneChoice>([
  LOCAL_ZONE_VALUE,
  ...TIME_ZONE_OPTIONS.map((o) => o.value),
]);

// localStorage throws in Safari private mode — fall back to the local default.
const readStoredTimeZone = (): TimeZoneChoice => {
  try {
    const stored = window.localStorage.getItem(TIME_ZONE_STORAGE_KEY);
    return stored && VALID_TIME_ZONES.has(stored) ? stored : LOCAL_ZONE_VALUE;
  } catch {
    return LOCAL_ZONE_VALUE;
  }
};

const storeTimeZone = (zone: TimeZoneChoice): void => {
  try {
    window.localStorage.setItem(TIME_ZONE_STORAGE_KEY, zone);
  } catch {
    // Best-effort: the picker still works for this session without persistence.
  }
  // Notify sibling charts already on screen so they follow the new choice too.
  window.dispatchEvent(new CustomEvent<TimeZoneChoice>(TIME_ZONE_EVENT, { detail: zone }));
};
interface GraphChartProps {
  // Opaque relative path from the <graph> tag, e.g. output/chart_flow_1.json.
  // In shared (read-only) views this is already a presigned URL.
  path: string;
  conversationId?: string;
  // Read-only (shared/public) context: suppress onboarding side effects.
  readOnly?: boolean;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; spec: GraphSpec; rows: CsvRow[]; csvText: string }
  | { status: 'error' };

const GraphChart: React.FC<GraphChartProps> = ({ path, conversationId, readOnly = false }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { capture } = usePostHogCapture();
  // Viewport-width mobile check (matches ProcessedScatterGraph) — decides
  // pinch-vs-drag zoom and whether panning is enabled, reactive to resize.
  const isMobile = useIsMobile();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<ChartJS | null>(null);
  const [legendOpen, setLegendOpen] = useState(false);
  // Indices of series toggled off via the legend popover.
  const [hidden, setHidden] = useState<Set<number>>(() => new Set());
  // Time-axis display zone. Defaults to the viewer's browser timezone; the
  // picker dropdown lets them switch to UTC or another zone (matches the raw
  // data / other tools).
  const [timeZone, setTimeZone] = useState<TimeZoneChoice>(readStoredTimeZone);
  const [timeZoneMenuOpen, setTimeZoneMenuOpen] = useState(false);
  const timeZoneRef = useRef<HTMLDivElement | null>(null);
  // Click-and-drag box zoom (chartjs-plugin-zoom), matching Asset View's
  // processed-model charts. Desktop viewports drag-to-zoom; mobile pinch + pan.
  // Reset is via double-click (desktop); no on-canvas button to avoid crowding.

  // Follow toggles from sibling charts in the same reply (setTimeZone only — no
  // re-store/re-dispatch, so this never loops back on the originating chart).
  useEffect(() => {
    const onZoneChange = (e: Event) => setTimeZone((e as CustomEvent<TimeZoneChoice>).detail);
    window.addEventListener(TIME_ZONE_EVENT, onZoneChange);
    return () => window.removeEventListener(TIME_ZONE_EVENT, onZoneChange);
  }, []);

  // Close the zone menu on outside click via a document listener (not a fixed
  // overlay — a full-viewport overlay swallows wheel events and blocks chat scroll).
  useEffect(() => {
    if (!timeZoneMenuOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!timeZoneRef.current?.contains(e.target as Node)) setTimeZoneMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [timeZoneMenuOpen]);

  // Fetch the spec + CSV. Chart.js config is built separately (below) so
  // switching the timezone picker doesn't require re-fetching.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // 1. Fetch + validate the spec JSON.
        const specUrl = toPublicUrl(path, conversationId);
        const specRes = await fetch(withCacheBuster(specUrl));
        if (!specRes.ok) throw new Error(`spec fetch failed: ${specRes.status}`);
        const specJson = await specRes.json();
        const spec: GraphSpec | null = validateGraphSpec(specJson);
        if (!spec) throw new Error('unsupported or invalid chart spec');

        // 2. Get the CSV. Shared snapshots embed it inline (the GCS file has
        //    expired); otherwise fetch + parse it from GCS (cached per conversation).
        const { rows, text } =
          spec.data.inline_csv != null
            ? parseCsvText(spec.data.inline_csv)
            : await fetchCsv(withCacheBuster(toPublicUrl(spec.data.csv_uri, conversationId)));

        if (!cancelled) setState({ status: 'ready', spec, rows, csvText: text });
      } catch (err) {
        console.warn('[GraphChart] failed to render graph', path, err);
        if (!cancelled) setState({ status: 'error' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [path, conversationId]);

  // Build the Chart.js config from the fetched spec/rows, in the selected
  // timezone. Recomputes on timezone toggle without re-fetching.
  const config = useMemo(() => {
    if (state.status !== 'ready') return null;
    return buildChartConfig(state.spec, state.rows, timeZone === LOCAL_ZONE_VALUE ? LOCAL_TIME_ZONE : timeZone);
  }, [state, timeZone]);
  const isTimeAxis = config?.options?.scales?.x?.type === 'time';

  // Render via a direct Chart.js instance on a canvas ref. (We manage the
  // instance ourselves rather than via react-chartjs-2's generic <Chart>
  // wrapper, which did not apply the data/options for mixed-type, multi-axis
  // object-data charts — leaving empty default 0–1 axes with no labels.)
  useEffect(() => {
    if (!config || !canvasRef.current) return;

    // Zoom options, matching Asset View's processed-model charts
    // (ProcessedScatterGraph.tsx / graphutil.ts's buildZoomOptions): Ctrl/⌘+scroll
    // to zoom, drag-to-zoom on desktop, pinch-to-zoom on mobile.
    const zoomOptions = {
      limits: { x: { min: 'original', max: 'original' }, y: { min: 'original', max: 'original' } },
      // Pan on mobile only, so a pinch-zoomed-in view can be navigated (matches Asset View).
      pan: { enabled: isMobile, mode: 'xy' },
      zoom: {
        wheel: { enabled: true, modifierKey: ZOOM_MODIFIER_KEY },
        drag: {
          enabled: !isMobile,
          backgroundColor: 'rgba(66, 165, 245, 0.1)',
          borderColor: 'rgba(66, 165, 245, 0.5)',
          borderWidth: 1,
          threshold: 5,
        },
        pinch: { enabled: isMobile },
        mode: 'xy',
      },
    };

    // datalabels is registered per-instance (NOT globally via registerables) so
    // point labels appear only on these AI Analyst charts, never on other
    // Chart.js charts in the app (ForecastGraph, percentile charts, etc.).
    let chart: ChartJS;
    try {
      chart = new ChartJS(canvasRef.current, {
        type: config.type,
        data: config.data,
        options: {
          ...config.options,
          plugins: { ...config.options.plugins, zoom: zoomOptions },
        },
        plugins: [ChartDataLabels],
      } as any);
    } catch (err) {
      // Chart.js throws synchronously on configs it can't handle. Uncaught in a
      // render effect, that unmounts the entire conversation view — degrade to
      // the "Chart unavailable" fallback instead (frontend-graph-rendering.md §8:
      // a chart failure must never break the surrounding chat).
      console.warn('[GraphChart] failed to construct chart', path, err);
      setState({ status: 'error' });
      return;
    }
    // Reapply series previously hidden via the legend popover — the timezone
    // picker destroys and rebuilds the chart instance (new tick/tooltip
    // formatters), which would otherwise reset every series back to visible.
    if (hidden.size > 0) {
      hidden.forEach((i) => chart.setDatasetVisibility(i, false));
      chart.update('none');
    }
    chartRef.current = chart;

    // Double-click resets zoom, mirroring the Asset View processed-model charts.
    const canvas = chart.canvas;
    const handleDoubleClick = () => {
      // resetZoom is attached to the Chart.js instance by chartjs-plugin-zoom
      // at runtime; its .d.ts augmentation isn't picked up here, hence `any`.
      (chart as any).resetZoom();
    };
    canvas.addEventListener('dblclick', handleDoubleClick);

    return () => {
      canvas.removeEventListener('dblclick', handleDoubleClick);
      chart.destroy();
      chartRef.current = null;
    };
    // `hidden` is deliberately omitted: it's read only to restore state onto a
    // freshly built chart. Including it would destroy/rebuild on every legend click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, path, isMobile]);

  if (state.status === 'loading') {
    return (
      <div className="bg-gray-50 rounded-lg py-4 px-3 border border-gray-200 mb-4 last:mb-0">
        <div className="h-[220px] flex items-center justify-center text-xs text-gray-400">
          Loading chart…
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="bg-gray-50 rounded-lg py-3 px-3 border border-gray-200 mb-4 last:mb-0">
        <div className="text-xs text-gray-500 text-center">Chart unavailable</div>
      </div>
    );
  }

  // Toggle a series on/off via the legend popover (labels still show on hover).
  const toggleSeries = (index: number) => {
    const chart = chartRef.current;
    if (!chart) return;
    const visible = chart.isDatasetVisible(index);
    chart.setDatasetVisibility(index, !visible);
    chart.update();
    setHidden((prev) => {
      const next = new Set(prev);
      if (visible) next.add(index);
      else next.delete(index);
      return next;
    });
  };

  const series = (config?.data.datasets ?? []).map((d: any) => ({
    label: d.label as string,
    color: (typeof d.borderColor === 'string' ? d.borderColor : '#888') as string,
  }));

  // Switch the time axis zone, remembering it for other charts and future visits.
  const selectTimeZone = (zone: TimeZoneChoice) => {
    setTimeZoneMenuOpen(false);
    // Re-selecting the already-active zone is a no-op — don't re-store or log it.
    if (zone === timeZone) return;
    setTimeZone(zone);
    storeTimeZone(zone);
    capture(POSTHOG_EVENTS.GRAPH_TIMEZONE_TOGGLED, { time_zone: zone, read_only: readOnly });
  };

  // Dropdown options: "Local" (the viewer's browser zone) first, then the
  // curated IANA zone list (UTC included).
  const timeZoneOptions = [
    { value: LOCAL_ZONE_VALUE, label: `Local (${LOCAL_TIME_ZONE})` },
    ...TIME_ZONE_OPTIONS,
  ];
  // Short label for the closed button (full description is in the dropdown
  // list) — the city segment of the IANA name keeps the button compact.
  const shortTimeZoneLabel = (zone: string): string => {
    if (zone === LOCAL_ZONE_VALUE) return 'Local';
    if (zone === 'UTC') return 'UTC';
    return (zone.split('/').pop() || zone).replace(/_/g, ' ');
  };

  const handleDownload = () => {
    if (state.status !== 'ready') return;
    const blob = new Blob([state.csvText], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeTitle = (state.spec.title || 'chart').replace(/\s+/g, '-').toLowerCase();
    a.download = `${safeTitle}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    // Don't advance the owner's onboarding checklist from a shared/public view.
    if (!readOnly) dispatch(markStepComplete(3));
  };

  return (
    <div className="bg-gray-50 rounded-lg py-4 px-3 border border-gray-200 relative mb-4 last:mb-0">
      {/* Reserve room for the control cluster so a long title truncates before
          sliding under the semi-transparent buttons. */}
      {state.spec.title && (
        <div className={`mb-3 ${isTimeAxis ? 'pr-36' : 'pr-24'}`}>
          <h4 className="text-sm font-medium text-gray-700 truncate">{state.spec.title}</h4>
        </div>
      )}

      <div className="absolute top-2 right-3 z-20 flex items-center gap-2">
        {/* Timezone picker: only shown for time-axis charts. Clicking it opens a
            dropdown of available zones (defaults to the viewer's browser zone). */}
        {isTimeAxis && (
          <div className="relative" ref={timeZoneRef}>
            <button
              onClick={() => setTimeZoneMenuOpen((open) => !open)}
              aria-expanded={timeZoneMenuOpen}
              aria-haspopup="listbox"
              className="h-8 px-2.5 bg-white/90 backdrop-blur-sm border border-gray-300/70 rounded-md shadow-sm hover:bg-white hover:border-gray-400 active:bg-gray-100 transition-colors flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
              title="Choose the time zone shown on this chart"
            >
              {shortTimeZoneLabel(timeZone)}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {timeZoneMenuOpen && (
              <div
                role="listbox"
                aria-label="Time zone"
                className="absolute top-9 right-0 z-20 bg-white border border-gray-200 rounded-md shadow-lg py-1 max-h-64 overflow-auto min-w-[220px]"
              >
                  {timeZoneOptions.map((option) => (
                    <button
                      key={option.value}
                      role="option"
                      aria-selected={timeZone === option.value}
                      onClick={() => selectTimeZone(option.value)}
                      className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                        timeZone === option.value
                          ? 'bg-gray-700 text-white'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Legend toggle: opens a list to turn channels on/off. Replaces the
            space-hungry inline legend; labels still appear on hover. */}
        <button
          onClick={() => setLegendOpen((open) => !open)}
          className="h-8 px-3 bg-white/90 backdrop-blur-sm border border-gray-300/70 rounded-md shadow-sm hover:bg-white hover:border-gray-400 active:bg-gray-100 transition-colors flex items-center justify-center text-xs font-medium text-gray-500 hover:text-gray-700"
          aria-expanded={legendOpen}
          title="Toggle series"
        >
          Legend
        </button>
      </div>
      {legendOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setLegendOpen(false)} />
          <div className="absolute top-11 right-3 z-20 bg-white border border-gray-200 rounded-md shadow-lg py-1 max-h-56 overflow-auto min-w-[160px] max-w-[220px]">
            {series.map((s, i) => {
              const isHidden = hidden.has(i);
              return (
                <button
                  key={i}
                  onClick={() => toggleSeries(i)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50"
                >
                  <span
                    className="w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: s.color, opacity: isHidden ? 0.3 : 1 }}
                  />
                  <span className={`truncate ${isHidden ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                    {s.label}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}

      <div style={{ height: 260 }}>
        <canvas ref={canvasRef} />
      </div>
      <button
        onClick={handleDownload}
        className="absolute bottom-2 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm border border-gray-300/70 rounded-md shadow-sm hover:bg-white hover:border-gray-400 transition-colors flex items-center justify-center group"
        title="Download CSV data"
      >
        <svg
          className="w-4 h-4 text-gray-500 group-hover:text-gray-700"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      </button>
    </div>
  );
};

export default GraphChart;
